import React from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { LuChevronRight, LuCopy } from 'react-icons/lu';
import { useTranslation } from '../../i18n/LanguageContext';

// Building blocks of the read-only "card" dialogs (Müşteri Kartı, Proje Kartı):
// labelled values with copy / link, titled sections and empty notes.

export const InfoItem = ({ label, value, hint, copyable, href, wide }) => {
  const { t } = useTranslation();
  const isEmpty = !value || value === '—' || value === '-';
  const shown = isEmpty ? '—' : value;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t('dataTable.panel.copied', { label }));
    } catch (error) {
      toast.error(t('dataTable.panel.copyFailed'));
    }
  };

  return (
    <div className={`tw-group tw-min-w-0 tw-rounded-xl tw-px-2.5 tw-py-2 tw-transition-colors hover:tw-bg-slate-900/[0.03] dark:hover:tw-bg-white/[0.03] ${wide ? 'sm:tw-col-span-2' : ''}`}>
      <p className="tw-truncate tw-text-[11px] tw-font-medium tw-text-slate-400 dark:tw-text-slate-500">{label}</p>
      <div className="tw-mt-0.5 tw-flex tw-min-w-0 tw-items-center tw-gap-1.5">
        {href && !isEmpty ? (
          <a href={href} title={shown} className="tw-min-w-0 tw-truncate tw-text-sm tw-font-medium tw-text-slate-800 tw-transition-colors hover:tw-text-teal-700 dark:tw-text-slate-100 dark:hover:tw-text-teal-300">
            {shown}
          </a>
        ) : (
          <span
            title={isEmpty ? undefined : shown}
            className={`tw-min-w-0 tw-text-sm tw-font-medium ${wide ? 'tw-break-words' : 'tw-truncate'} ${isEmpty ? 'tw-text-slate-400 dark:tw-text-slate-500' : 'tw-text-slate-800 dark:tw-text-slate-100'}`}
          >
            {shown}
          </span>
        )}
        {hint && <span className="tw-shrink-0 tw-text-xs tw-text-slate-400 dark:tw-text-slate-500">· {hint}</span>}
        {copyable && !isEmpty && (
          <button
            type="button"
            onClick={copy}
            title={t('dataTable.panel.copyLabel', { label })}
            aria-label={t('dataTable.panel.copyLabel', { label })}
            className="tw-ml-auto tw-grid tw-h-6 tw-w-6 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 tw-opacity-0 tw-transition hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 focus-visible:tw-opacity-100 group-hover:tw-opacity-100 [@media(hover:none)]:tw-opacity-100 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
          >
            <LuCopy aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

InfoItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  hint: PropTypes.string,
  copyable: PropTypes.bool,
  href: PropTypes.string,
  wide: PropTypes.bool,
};

export const InfoSection = ({ icon: Icon, title, action, children }) => (
  <section className="tw-block">
    <div className="tw-mb-1.5 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-2.5">
      <p className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">
        <Icon aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
        {title}
      </p>
      {action}
    </div>
    <div className="tw-grid tw-gap-x-2 tw-gap-y-0.5 sm:tw-grid-cols-2">{children}</div>
  </section>
);

InfoSection.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  action: PropTypes.node,
  children: PropTypes.node,
};

export const ManageLink = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="tw-inline-flex tw-items-center tw-gap-0.5 tw-rounded-full tw-bg-transparent tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-teal-700 tw-transition-colors hover:tw-bg-teal-500/10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-text-teal-300"
  >
    {label}
    <LuChevronRight aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
  </button>
);

ManageLink.propTypes = { label: PropTypes.string.isRequired, onClick: PropTypes.func.isRequired };

export const EmptyNote = ({ children }) => (
  <p className="tw-col-span-full tw-px-2.5 tw-py-2 tw-text-sm tw-text-slate-400 dark:tw-text-slate-500">{children}</p>
);

EmptyNote.propTypes = { children: PropTypes.node };
